"use client";

import Link from "next/link";
import { Users, Wrench, ArrowRight, Check, type LucideIcon } from "lucide-react";
import { motion, useReducedMotion, type TargetAndTransition } from "framer-motion";
import SectionEyebrow from "@/components/ui/SectionEyebrow";
import FAQAccordion from "@/components/ui/FAQAccordion";
import Footer from "@/components/layout/Footer";
import { platformFaqs } from "@/lib/faqs";

type PathAccent = "gold" | "green";

const PATH_ACCENTS: Record<PathAccent, string> = {
  gold: "hsl(45 100% 58%)",
  green: "hsl(142 70% 55%)",
};

function PathCard({
  href,
  Icon,
  accent,
  badge,
  heading,
  description,
  bullets,
  ctaLabel,
  delay,
  anim,
}: {
  href: string;
  Icon: LucideIcon;
  accent: PathAccent;
  badge: string;
  heading: string;
  description: string;
  bullets: string[];
  ctaLabel: string;
  delay: number;
  anim: TargetAndTransition;
}) {
  return (
    <motion.div
      initial={anim}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="h-full"
    >
      <Link
        href={href}
        className="path-card group block h-full rounded-[28px] p-8 md:p-10"
        style={{ "--path-accent": PATH_ACCENTS[accent] } as React.CSSProperties}
      >
        <div className="relative">
          <div className="flex items-center gap-3 mb-6">
            <div className="path-icon-tile w-14 h-14 rounded-2xl flex items-center justify-center shrink-0">
              <Icon size={24} aria-hidden="true" />
            </div>
            <span className="path-badge font-accent text-[10px] font-medium uppercase tracking-wider rounded-full px-3 py-1.5">
              {badge}
            </span>
          </div>

          <h2 className="path-card-heading font-heading font-semibold text-[22px] md:text-[30px] mb-3 leading-tight tracking-tight">
            {heading}
          </h2>
          <p className="text-[14px] md:text-[15px] text-on-plate/60 leading-relaxed mb-7 max-w-[38ch]">
            {description}
          </p>

          <div
            className="h-px w-full mb-6"
            style={{
              background:
                "linear-gradient(90deg, color-mix(in srgb, var(--path-accent) 35%, transparent), transparent)",
            }}
            aria-hidden="true"
          />

          <ul className="space-y-3 mb-8">
            {bullets.map((item) => (
              <li
                key={item}
                className="flex items-start gap-2.5 text-[13.5px] text-on-plate/75 leading-snug"
              >
                <Check
                  size={15}
                  strokeWidth={2.5}
                  className="path-check mt-[2px] shrink-0"
                  aria-hidden="true"
                />
                {item}
              </li>
            ))}
          </ul>

          <div className="path-cta inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-heading text-[13.5px] font-semibold">
            {ctaLabel} <ArrowRight size={14} />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function PlatformPage() {
  const shouldReduceMotion = useReducedMotion();
  const anim = shouldReduceMotion ? {} : { opacity: 0, y: 16 };

  return (
    <main id="main-content" className="bg-section">
      <section className="relative overflow-hidden pt-28 md:pt-32 pb-20" aria-labelledby="hero-heading">
        <div className="relative z-10 px-6 md:px-12 max-w-[1400px] w-full mx-auto text-center">
          <SectionEyebrow label="The platform" />
          <motion.h1
            id="hero-heading"
            className="font-heading mx-auto"
            style={{
              fontSize: "clamp(2.5rem, 1rem + 6vw, 6rem)",
              lineHeight: 0.92,
              letterSpacing: "-0.045em",
              fontWeight: 450,
            }}
            initial={anim}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            One platform.
            <br className="md:hidden" />{" "}
            <span className="gold-gradient-text">Two ways in.</span>
          </motion.h1>
          <motion.p
            className="text-[15px] md:text-[17px] text-muted-foreground max-w-2xl mx-auto mt-6 leading-snug md:whitespace-nowrap"
            initial={anim}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            Use AI agents to run your firm. Or build agents and sell them in our store. Pick your path.
          </motion.p>
        </div>
      </section>

      <section className="pb-20 md:pb-28" aria-label="Choose your path">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7 max-w-5xl mx-auto items-stretch">
            <PathCard
              href="/platform/teams"
              Icon={Users}
              accent="gold"
              badge="For professionals & teams"
              heading="Run your firm with AI"
              description="Meeting transcription, email management, knowledge base, and pre-built specialist agents. Your AI workspace, ready out of the box."
              bullets={[
                "Meeting recaps + follow-ups",
                "Email triage + scheduling",
                "Firm-wide knowledge search",
                "Install agents from the store",
              ]}
              ctaLabel="See what teams get"
              delay={0.3}
              anim={anim}
            />

            <PathCard
              href="/platform/builders"
              Icon={Wrench}
              accent="green"
              badge="For builders"
              heading="Build AI agents. Earn"
              description="Build no-code agents, list them in the store, and earn from every install. We handle distribution, billing, and compliance."
              bullets={[
                "No-code agent builder",
                "Multi-model architecture",
                "List in the marketplace",
                "Earn from every install",
              ]}
              ctaLabel="See what builders get"
              delay={0.4}
              anim={anim}
            />
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-28" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-[860px] px-6 md:px-12 text-center">
          <motion.h2
            id="faq-heading"
            className="font-heading heading-pure mb-12"
            style={{
              fontSize: "clamp(1.75rem, 1rem + 2.5vw, 2.75rem)",
              lineHeight: 1.05,
              letterSpacing: "-0.035em",
              fontWeight: 450,
            }}
            initial={anim}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            Common questions
          </motion.h2>
          <div className="text-left">
            <FAQAccordion faqs={platformFaqs} />
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
